/**
 * Gestionnaire de Projets pour le viewer 3D.
 *
 * Ce gestionnaire orchestre le chargement et l'affichage des projets:
 * - fond de carte LIDAR partagé
 * - volume de gabarit (optionnel)
 * - sélection indépendante des soumissions BUILDING / LANDSCAPE
 * - modes d'affichage Avant / Après / Solo
 */

import {
  Parcel,
  Project,
  ProjectOverlayAlignmentMetadata,
  ProjectOverlayLayer,
  ProjectState,
  SubmissionType,
  TeamSubmission,
  ViewMode,
} from "@/types/project";
import type { ViewerLayerControlKey } from "@/types/project";

import CesiumManager from "./CesiumManager";
import { LayerManager } from "./LayerManager";
import type { ModelLoadingProgress } from "./types";
import type { Season } from "@/stores/solarSimulationStore";
import { useSolarSimulationStore } from "@/stores/solarSimulationStore";
import { useAuthStore } from "@/stores/authStore";
import {
  useSelectionPersistenceStore,
  getSelectionsToRestore,
} from "@/stores/selectionPersistenceStore";
import { useDebugSettingsStore } from "@/stores/debugSettingsStore";

type ParcelStaticLayerType = "basemap" | "gabarit" | "overlay";

interface OrderedStaticLayerItem {
  layer:
    | Project["parcels"][number]["basemapLayer"]
    | Project["parcels"][number]["gabaritLayer"]
    | ProjectOverlayLayer;
  projectId: string;
  context?: {
    referenceWgs84: Parcel["referenceWgs84"];
    solarAnchor?: {
      longitude: number;
      latitude: number;
    } | null;
    solarContext?: {
      hour: number;
      season: Season;
    };
    referenceRotationDeg: Parcel["refRotation"];
    referencePitchDeg: Parcel["refPitch"];
    referenceRollDeg: Parcel["refRoll"];
    overlayAlignment?: ProjectOverlayAlignmentMetadata | null;
  };
  staticType: ParcelStaticLayerType;
}

export interface InitialSubmissionsProgress {
  totalCount: number;
  loadedCount: number;
  loadedEquivalentCount: number;
  currentSubmissionType: SubmissionType | null;
  currentSubmissionProgress?: number;
  progressIsIndeterminate?: boolean;
  submissions: Partial<
    Record<
      SubmissionType,
      {
        progress: number;
        determinate: boolean;
        loaded: boolean;
      }
    >
  >;
}

export interface ProjectLoadCallbacks {
  selectFirstSubmissions?: boolean;
  allowMultipleSubmissions?: boolean;
  hideGabarit?: boolean;
  onStaticLayersReady?: () => void;
  onInitialSubmissionsProgress?: (progress: InitialSubmissionsProgress) => void;
}

interface InitialSubmissionLoadTarget {
  submission: TeamSubmission;
  parcel: Parcel;
  setAsCurrent: boolean;
}

interface LoadSubmissionForParcelOptions {
  setAsCurrent?: boolean;
}

interface ApplyViewModeOptions {
  preserveGabaritVisibility?: boolean;
}

interface SubmissionLoadResult {
  success: boolean;
  errorMessage?: string;
}

const DEFAULT_PROJECT_OVERLAY_ALIGNMENT: ProjectOverlayAlignmentMetadata = {
  version: 1,
  eastMeters: 0,
  northMeters: 0,
  upMeters: 0,
  headingDegrees: 0,
  pitchDegrees: 0,
  rollDegrees: 0,
};

function getFiniteAlignmentValue(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function getProjectOverlayAlignment(
  overlayLayer: ProjectOverlayLayer,
): ProjectOverlayAlignmentMetadata {
  const alignment = overlayLayer.metadata?.positionAlignment;

  return {
    ...DEFAULT_PROJECT_OVERLAY_ALIGNMENT,
    eastMeters: getFiniteAlignmentValue(alignment?.eastMeters),
    northMeters: getFiniteAlignmentValue(alignment?.northMeters),
    upMeters: getFiniteAlignmentValue(alignment?.upMeters),
    headingDegrees: getFiniteAlignmentValue(alignment?.headingDegrees),
    pitchDegrees: getFiniteAlignmentValue(alignment?.pitchDegrees),
    rollDegrees: getFiniteAlignmentValue(alignment?.rollDegrees),
  };
}

export class ProjectManager {
  private layerManager: LayerManager;
  private cesiumManager: CesiumManager;
  private state: ProjectState;
  private onStateChangeCallbacks: Array<(state: ProjectState) => void> = [];
  private selectionOrder: string[] = [];
  private selectFirstSubmissions = false;
  private submissionLoadCount = 0;
  private mutationQueue: Promise<unknown> = Promise.resolve();

  // Les mutations de scène restent ordonnées, même après un chargement en erreur.
  private enqueueMutation<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.mutationQueue.then(operation);
    this.mutationQueue = result.catch(() => undefined);
    return result;
  }

  /** Change uniquement les propositions ; le décor et la caméra sont conservés. */
  setConsultedPhase(project: Project, phase: { hideGabarit?: boolean; allowMultipleSubmissions?: boolean }, selectedIds?: string[]): Promise<void> {
    return this.enqueueMutation(async () => {
      if (this.state.project?.id !== project.id) return;
      const parcelId = this.state.currentParcel?.id;
      await this.unloadCurrentSubmissionLayers();
      this.submissionSelectionByParcelId.clear();
      useSelectionPersistenceStore.getState().clearProjectSelections(project.id);
      this.updateState({ project, currentParcel: project.parcels.find(p => p.id === parcelId) ?? project.parcels[0] ?? null,
        currentBuildingSubmission: null, currentLandscapeSubmission: null, selectedSubmissionIds: [], submissionErrors: {},
        buildingSubmissionError: null, landscapeSubmissionError: null,
        allowMultipleSubmissions: phase.allowMultipleSubmissions ?? false });
      this.setPhaseGabaritHidden(phase.hideGabarit ?? false);
      for (const parcel of project.parcels) {
        for (const type of ["BUILDING", "LANDSCAPE"] as const) {
          const available = parcel.submissions.filter(s => s.submissionType === type);
          const remembered = selectedIds?.map(id => available.find(s => s.id === id)).filter((s): s is TeamSubmission => Boolean(s));
          const targets = selectedIds === undefined ? available.slice(0, 1) : remembered ?? [];
          for (const submission of (phase.allowMultipleSubmissions ? targets : targets.slice(-1))) {
            await this.loadSubmissionForParcel(submission, parcel, undefined, { setAsCurrent: parcel.id === parcelId });
          }
        }
      }
      this.applyViewMode(this.state.viewMode);
    });
  }

  setPhaseBehaviors(phase?: { hideGabarit?: boolean; allowMultipleSubmissions?: boolean } | null): Promise<void> {
    return this.enqueueMutation(async () => {
      const multiple = phase?.allowMultipleSubmissions ?? false;
      if (this.state.allowMultipleSubmissions && !multiple) {
        // Une sortie de phase repart sans aucune proposition, sans toucher aux overlays.
        await this.unloadCurrentSubmissionLayers();
        this.submissionSelectionByParcelId.clear();
        if (this.state.project) useSelectionPersistenceStore.getState().clearProjectSelections(this.state.project.id);
        this.updateState({ currentBuildingSubmission: null, currentLandscapeSubmission: null, submissionErrors: {} });
      }
      this.updateState({ allowMultipleSubmissions: multiple });
      this.setPhaseGabaritHidden(phase?.hideGabarit ?? false);
    });
  }
  private loadedSubmissionByParcelId: Map<
    string,
    Partial<Record<SubmissionType, TeamSubmission[]>>
  > = new Map();
  private submissionSelectionByParcelId: Map<
    string,
    Partial<Record<SubmissionType, string>>
  > = new Map();

  constructor() {
    this.layerManager = new LayerManager();
    this.cesiumManager = CesiumManager.getInstance();

    // Charger les sélections persistées depuis sessionStorage
    useSelectionPersistenceStore.getState().loadFromStorage();

    this.state = {
      project: null,
      currentParcel: null,
      currentBuildingSubmission: null,
      currentLandscapeSubmission: null,
      isSubmissionLoading: false,
      buildingSubmissionError: null,
      landscapeSubmissionError: null,
      viewMode: "after",
      basemapVisible: true,
      basemapOpacity: 1.0,
      gabaritsLoaded: false,
      allowMultipleSubmissions: false,
      selectedSubmissionIds: [],
      submissionErrors: {},
      gabaritHiddenByPhase: false,
      gabaritVisible: false,
      gabaritOpacity: 1.0,
      architectureVisible: true,
      architectureOpacity: 1.0,
    };
  }

  loadProject(project: Project, callbacks: ProjectLoadCallbacks = {}): Promise<void> {
    return this.enqueueMutation(() => this.loadProjectInternal(project, callbacks));
  }

  private async loadProjectInternal(
    project: Project,
    callbacks: ProjectLoadCallbacks = {},
  ): Promise<void> {
    if (this.state.project) {
      await this.unloadProjectInternal();
    }

    console.log(`[ProjectManager] Chargement du projet "${project.name}"`);

    this.selectFirstSubmissions = callbacks.selectFirstSubmissions ?? false;
    const persistedSelections = getSelectionsToRestore(project.id).filter((selection) => project.parcels.some((parcel) =>
      parcel.id === selection.parcelId && parcel.submissions.some((submission) => submission.id === selection.submissionId && submission.submissionType === selection.type),
    ));
    useSelectionPersistenceStore.getState().pruneInvalidSelections(project.id, new Set(project.parcels.flatMap((parcel) => parcel.submissions.map((submission) => submission.id))));
    const currentParcel =
      project.parcels.length > 0 ? project.parcels[0] : null;
    this.updateState({ project, currentParcel, allowMultipleSubmissions: callbacks.allowMultipleSubmissions ?? false, gabaritHiddenByPhase: callbacks.hideGabarit ?? false });

    // Recharger les paramètres uniquement s'ils ne sont pas déjà en mémoire
    // pour ce projet (ex: bootstrap initial).
    if (useDebugSettingsStore.getState().currentProjectId !== project.id) {
      void useDebugSettingsStore.getState().loadSettings(project.id);
    }

    if (!currentParcel) {
      console.warn(
        `[ProjectManager] Le projet "${project.name}" n'a pas de parcelle`,
      );
      return;
    }

    // Positionner la caméra au chargement du projet (une seule fois ici),
    // sans dépendre des événements de chargement du basemap.
    this.positionCameraFromProjectDefaults(project);

    // Pendant le bootstrap initial, on suspend le LOD basemap pour limiter
    // les décodages Draco concurrents avec les soumissions.
    this.layerManager.setDeferredLodStart(true);

    try {
      await this.loadProjectStaticLayersInExplicitOrder(project, {
        includeGabarits: false,
      });
      callbacks.onStaticLayersReady?.();

      const initialSubmissionTargets = project.parcels.flatMap((parcel) => {
        const targets: InitialSubmissionLoadTarget[] = [];
        const setAsCurrent = parcel.id === currentParcel.id;
        const firstBuilding = this.findSelectedSubmissionByType(
          parcel,
          "BUILDING",
        );
        const firstLandscape = this.findSelectedSubmissionByType(
          parcel,
          "LANDSCAPE",
        );

        if (firstBuilding) {
          targets.push({
            submission: firstBuilding,
            parcel,
            setAsCurrent,
          });
        }

        if (firstLandscape) {
          targets.push({
            submission: firstLandscape,
            parcel,
            setAsCurrent,
          });
        }

        return targets;
      });

      await this.loadInitialSubmissionsWithProgress(
        initialSubmissionTargets,
        callbacks.onInitialSubmissionsProgress,
      );
      this.applyViewMode(this.state.viewMode, {
        preserveGabaritVisibility: true,
      });

      // Restauration des sélections persistées après le chargement initial
      // pour les cas où aucune sélection explicite n'existait dans les données
      await this.restorePersistedSelections(persistedSelections);
    } finally {
      this.layerManager.setDeferredLodStart(false);
    }

    console.log(`[ProjectManager] Projet "${project.name}" chargé avec succès`);
  }

  private async loadInitialSubmissionsWithProgress(
    initialSubmissions: InitialSubmissionLoadTarget[],
    onProgress?: (progress: InitialSubmissionsProgress) => void,
  ): Promise<void> {
    const totalCount = initialSubmissions.length;
    const progressionParSoumissionId = new Map<
      string,
      {
        submissionType: SubmissionType;
        progress: number;
        determinate: boolean;
        loaded: boolean;
      }
    >();

    const calculerProgressionParType =
      (): InitialSubmissionsProgress["submissions"] => {
        const progressionParType: InitialSubmissionsProgress["submissions"] =
          {};

        for (const type of ["BUILDING", "LANDSCAPE"] as const) {
          const progressions = Array.from(
            progressionParSoumissionId.values(),
          ).filter((progression) => progression.submissionType === type);

          if (progressions.length === 0) {
            continue;
          }

          const progressCumule = progressions.reduce((total, progression) => {
            if (progression.loaded) {
              return total + 1;
            }

            return total + Math.max(0, Math.min(progression.progress, 1));
          }, 0);

          progressionParType[type] = {
            progress: progressCumule / progressions.length,
            determinate: progressions.every(
              (progression) => progression.loaded || progression.determinate,
            ),
            loaded: progressions.every((progression) => progression.loaded),
          };
        }

        return progressionParType;
      };

    const notifierProgression = (
      overrides: Partial<InitialSubmissionsProgress> = {},
    ) => {
      const loadedCount = Array.from(
        progressionParSoumissionId.values(),
      ).filter((progression) => progression?.loaded).length;
      const loadedEquivalentCount = Array.from(
        progressionParSoumissionId.values(),
      ).reduce((total, progression) => {
        if (progression.loaded) {
          return total + 1;
        }

        return total + Math.max(0, Math.min(progression.progress, 1));
      }, 0);

      onProgress?.({
        totalCount,
        loadedCount,
        loadedEquivalentCount,
        currentSubmissionType: null,
        currentSubmissionProgress: 0,
        progressIsIndeterminate: false,
        submissions: calculerProgressionParType(),
        ...overrides,
      });
    };

    notifierProgression();

    if (totalCount === 0) {
      return;
    }

    await Promise.all(
      initialSubmissions.map(async ({ submission, parcel, setAsCurrent }) => {
        progressionParSoumissionId.set(submission.id, {
          submissionType: submission.submissionType,
          progress: 0,
          determinate: false,
          loaded: false,
        });
        notifierProgression({
          currentSubmissionType: submission.submissionType,
          currentSubmissionProgress: 0,
          progressIsIndeterminate: true,
        });

        const result = await this.loadSubmissionForParcel(
          submission,
          parcel,
          (modelProgress) => {
            progressionParSoumissionId.set(submission.id, {
              submissionType: submission.submissionType,
              progress: modelProgress.progress,
              determinate: modelProgress.determinate,
              loaded: false,
            });
            notifierProgression({
              currentSubmissionType: submission.submissionType,
              currentSubmissionProgress: modelProgress.progress,
              progressIsIndeterminate: !modelProgress.determinate,
            });
          },
          { setAsCurrent },
        );

        if (!result.success) {
          progressionParSoumissionId.set(submission.id, {
            submissionType: submission.submissionType,
            progress: 0,
            determinate: true,
            loaded: false,
          });
          notifierProgression({
            currentSubmissionType: submission.submissionType,
            currentSubmissionProgress: 0,
            progressIsIndeterminate: false,
          });
          return;
        }

        progressionParSoumissionId.set(submission.id, {
          submissionType: submission.submissionType,
          progress: 1,
          determinate: true,
          loaded: true,
        });
        notifierProgression({
          currentSubmissionType: submission.submissionType,
          currentSubmissionProgress: 1,
          progressIsIndeterminate: false,
        });
      }),
    );
  }

  private positionCameraFromProjectDefaults(project: Project): void {
    const hasDefaultCamera =
      typeof project.defaultLatitude === "number" &&
      Number.isFinite(project.defaultLatitude) &&
      typeof project.defaultLongitude === "number" &&
      Number.isFinite(project.defaultLongitude) &&
      typeof project.defaultAltitude === "number" &&
      Number.isFinite(project.defaultAltitude);

    if (!hasDefaultCamera) {
      return;
    }

    this.cesiumManager.flyToLocation(
      project.defaultLatitude!,
      project.defaultLongitude!,
      project.defaultAltitude!,
      0,
      project.defaultPitch,
    );
  }

  updateCurrentProject(project: Project): void {
    if (!this.state.project || this.state.project.id !== project.id) {
      return;
    }

    const currentParcel =
      project.parcels.find(
        (parcel) => parcel.id === this.state.currentParcel?.id,
      ) ?? (project.parcels.length > 0 ? project.parcels[0] : null);

    this.updateState({
      project,
      currentParcel,
    });
  }

  unloadProject(): Promise<void> {
    return this.enqueueMutation(() => this.unloadProjectInternal());
  }

  private async unloadProjectInternal(): Promise<void> {
    if (!this.state.project) return;

    const project = this.state.project;

    await this.unloadCurrentSubmissionLayers();
    await this.unloadStaticLayers(project);

    this.cesiumManager.setSwissTopoBackgroundVisibility(true);

    this.updateState({
      project: null,
      currentParcel: null,
      currentBuildingSubmission: null,
      currentLandscapeSubmission: null,
      isSubmissionLoading: false,
      buildingSubmissionError: null,
      landscapeSubmissionError: null,
      viewMode: "after",
      basemapVisible: true,
      basemapOpacity: 1.0,
      gabaritsLoaded: false,
      allowMultipleSubmissions: false,
      selectedSubmissionIds: [],
      submissionErrors: {},
      gabaritHiddenByPhase: false,
      gabaritVisible: false,
      gabaritOpacity: 1.0,
      architectureVisible: true,
      architectureOpacity: 1.0,
    });
    this.submissionSelectionByParcelId.clear();
    this.loadedSubmissionByParcelId.clear();

    // Nettoyer les paramètres de debug
    useDebugSettingsStore.getState().clear();
  }

  loadSubmission(submission: TeamSubmission, onModelLoadingProgress?: (progress: ModelLoadingProgress) => void): Promise<SubmissionLoadResult> {
    return this.enqueueMutation(() => this.loadSubmissionInternal(submission, onModelLoadingProgress));
  }

  private async loadSubmissionInternal(
    submission: TeamSubmission,
    onModelLoadingProgress?: (progress: ModelLoadingProgress) => void,
  ): Promise<SubmissionLoadResult> {
    if (!this.state.currentParcel) {
      return {
        success: false,
        errorMessage: "Aucune parcelle active pour charger la soumission.",
      };
    }
    return await this.loadSubmissionForParcel(
      submission,
      this.state.currentParcel,
      onModelLoadingProgress,
    );
  }

  switchToSubmission(submissionId: string, onModelLoadingProgress?: (progress: ModelLoadingProgress) => void): Promise<SubmissionLoadResult> {
    return this.enqueueMutation(() => this.switchToSubmissionInternal(submissionId, onModelLoadingProgress));
  }

  private async switchToSubmissionInternal(
    submissionId: string,
    onModelLoadingProgress?: (progress: ModelLoadingProgress) => void,
  ): Promise<SubmissionLoadResult> {
    if (!this.state.project) {
      return {
        success: false,
        errorMessage: "Aucun projet actif.",
      };
    }

    const submissionWithParcel = this.findSubmissionWithParcel(submissionId);
    if (!submissionWithParcel) {
      return {
        success: false,
        errorMessage: "Soumission introuvable.",
      };
    }

    const { submission, parcel } = submissionWithParcel;
    const currentLoadedSubmission = this.loadedSubmissionByParcelId.get(parcel.id)?.[submission.submissionType]?.find((item) => item.id === submission.id) ?? null;
    const shouldForceReload = this.shouldForceReloadSubmission(
      currentLoadedSubmission,
      submission,
    );

    if (currentLoadedSubmission?.id === submission.id && !shouldForceReload) {
      await this.unloadSubmission(submission, parcel.id);
      return { success: true };
    }

    return await this.loadSubmissionForParcel(
      submission,
      parcel,
      onModelLoadingProgress,
    );
  }

  switchToTeamSubmission(teamId: string): Promise<void> {
    return this.enqueueMutation(() => this.switchToTeamSubmissionInternal(teamId));
  }

  private async switchToTeamSubmissionInternal(teamId: string): Promise<void> {
    if (!this.state.currentParcel) return;

    const submission = this.state.currentParcel.submissions.find(
      (s) => s.teamId === teamId,
    );
    if (!submission) return;

    const result = await this.loadSubmissionForParcel(
      submission,
      this.state.currentParcel,
    );

    if (!result.success && result.errorMessage) {
      throw new Error(result.errorMessage);
    }
  }

  setViewMode(mode: ViewMode): void {
    this.updateState({ viewMode: mode });
    this.applyViewMode(mode);
  }

  private applyViewMode(
    mode: ViewMode,
    options: ApplyViewModeOptions = {},
  ): void {
    switch (mode) {
      case "before":
        this.showBeforeState();
        break;
      case "after":
        this.showAfterState(options);
        break;
      case "solo":
        this.showSoloMode();
        break;
    }
  }

  private applyTerritoryVisibility(visible: boolean): void {
    this.applyToProjectParcelLayers("basemap", (layerId) => {
      this.layerManager.setLayerVisibility(layerId, visible);
    });
    this.applyToProjectOverlayLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, visible);
    });
    this.cesiumManager.setSwissTopoBackgroundVisibility(visible);
  }

  private showBeforeState(): void {
    this.applyTerritoryVisibility(true);

    this.applyToProjectParcelLayers("gabarit", (layerId) => {
      this.layerManager.setLayerVisibility(layerId, false);
    });

    this.applyToProjectOverlayLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, false);
    });

    this.applyToActiveArchitectureLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, false);
    });

    this.updateState({
      basemapVisible: true,
      gabaritVisible: false,
      architectureVisible: false,
    });
  }

  setPhaseGabaritHidden(hidden: boolean): void {
    if (hidden === Boolean(this.state.gabaritHiddenByPhase)) return;
    this.updateState({ gabaritHiddenByPhase: hidden });
    // Ne décharge aucun modèle : seule la visibilité change avec la phase.
    this.applyViewMode(this.state.viewMode);
    this.cesiumManager.requestRender();
  }

  private showAfterState(options: ApplyViewModeOptions = {}): void {
    this.applyTerritoryVisibility(true);
    const shouldShowGabarits = !this.state.gabaritHiddenByPhase && (options.preserveGabaritVisibility
      ? this.state.gabaritsLoaded && this.state.gabaritVisible
      : this.state.gabaritsLoaded);

    this.applyToProjectParcelLayers("gabarit", (layerId) => {
      this.layerManager.setLayerVisibility(layerId, shouldShowGabarits);
    });

    this.applyToProjectOverlayLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, true);
    });

    this.applyToActiveArchitectureLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, true);
    });

    this.updateState({
      basemapVisible: true,
      gabaritVisible: shouldShowGabarits,
      architectureVisible: true,
    });
  }

  private showSoloMode(): void {
    this.applyTerritoryVisibility(false);

    this.applyToProjectParcelLayers("gabarit", (layerId) => {
      this.layerManager.setLayerVisibility(layerId, false);
    });

    this.applyToProjectOverlayLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, true);
    });

    this.applyToActiveArchitectureLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, true);
    });

    this.updateState({
      basemapVisible: false,
      gabaritVisible: false,
      architectureVisible: true,
    });
  }

  toggleBasemapVisibility(): void {
    if (!this.hasProjectParcelLayer("basemap")) return;

    const newVisibility = !this.state.basemapVisible;
    this.applyTerritoryVisibility(newVisibility);
    this.updateState({ basemapVisible: newVisibility });
  }

  async toggleAllGabarits(): Promise<void> {
    if (this.state.gabaritHiddenByPhase) return;
    this.cesiumManager.requestRender();

    if (!this.hasProjectParcelLayer("gabarit") || !this.state.project) {
      return;
    }

    const layerIds = this.getProjectParcelLayerIds("gabarit");

    if (this.state.gabaritsLoaded) {
      await this.unloadAllGabarits();
      return;
    }

    const orderedItems = this.getOrderedStaticLayerItems(
      this.state.project,
    ).filter((item) => item.staticType === "gabarit");

    let loadedCount = 0;

    for (const item of orderedItems) {
      if (!item.layer) {
        continue;
      }

      try {
        await this.layerManager.loadLayer(item.layer, item.projectId, {
          referenceWgs84: item.context?.referenceWgs84,
          solarAnchor: item.context?.solarAnchor,
          solarContext: item.context?.solarContext,
          referenceRotationDeg: item.context?.referenceRotationDeg,
          referencePitchDeg: item.context?.referencePitchDeg,
          referenceRollDeg: item.context?.referenceRollDeg,
        });
        this.layerManager.setLayerOpacity(item.layer.id, this.state.gabaritOpacity);
        loadedCount += 1;
      } catch (error) {
        console.error(
          "[ProjectManager] Erreur lors du chargement d'un gabarit:",
          error,
        );
      }
    }

    if (loadedCount === 0) {
      this.updateState({
        gabaritsLoaded: false,
        gabaritVisible: false,
      });
      return;
    }

    // Le panneau Couches doit avoir le dernier mot: lorsqu'on coche
    // "Gabarits", on charge et on affiche explicitement tous les gabarits.
    for (const layerId of layerIds) {
      if (!this.layerManager.getLayerState(layerId)) {
        continue;
      }
      this.layerManager.setLayerVisibility(layerId, !this.state.gabaritHiddenByPhase);
    }

    this.updateState({
      gabaritsLoaded: true,
      gabaritVisible: !this.state.gabaritHiddenByPhase,
    });

    this.cesiumManager.requestRender();
  }

  toggleGabaritVisibility(): void {
    if (this.state.gabaritHiddenByPhase) return;
    if (!this.state.gabaritsLoaded || !this.hasProjectParcelLayer("gabarit")) {
      return;
    }

    const newVisibility = !this.state.gabaritVisible;
    this.applyToProjectParcelLayers("gabarit", (layerId) => {
      this.layerManager.setLayerVisibility(layerId, newVisibility);
    });
    this.cesiumManager.requestRender();
    this.updateState({ gabaritVisible: newVisibility });
  }

  toggleArchitectureVisibility(): void {
    if (!this.hasActiveArchitectureLayers()) return;

    const newVisibility = !this.state.architectureVisible;
    this.applyToActiveArchitectureLayers((layerId) => {
      this.layerManager.setLayerVisibility(layerId, newVisibility);
    });

    this.updateState({ architectureVisible: newVisibility });
  }

  setLayerOpacity(layerKey: ViewerLayerControlKey, opacity: number): void {
    const clampedOpacity = Math.max(0, Math.min(1, opacity));

    switch (layerKey) {
      case "basemap":
        if (this.hasProjectParcelLayer("basemap")) {
          this.applyToProjectParcelLayers("basemap", (layerId) => {
            this.layerManager.setLayerOpacity(layerId, clampedOpacity);
          });
        }
        this.updateState({ basemapOpacity: clampedOpacity });
        return;
      case "gabarit":
        if (this.hasProjectParcelLayer("gabarit")) {
          this.applyToProjectParcelLayers("gabarit", (layerId) => {
            this.layerManager.setLayerOpacity(layerId, clampedOpacity);
          });
        }
        this.updateState({ gabaritOpacity: clampedOpacity });
        return;
      case "project":
        if (this.hasActiveArchitectureLayers()) {
          this.applyToActiveArchitectureLayers((layerId) => {
            this.layerManager.setLayerOpacity(layerId, clampedOpacity);
          });
        }
        this.updateState({ architectureOpacity: clampedOpacity });
        return;
    }
  }

  setBasemapOpacity(opacity: number): void {
    this.setLayerOpacity("basemap", opacity);
  }

  setGabaritOpacity(opacity: number): void {
    this.setLayerOpacity("gabarit", opacity);
  }

  setArchitectureOpacity(opacity: number): void {
    this.setLayerOpacity("project", opacity);
  }

  getState(): ProjectState {
    return { ...this.state };
  }

  getCurrentProject(): Project | null {
    return this.state.project;
  }

  getCurrentSubmission(): TeamSubmission | null {
    return (
      this.state.currentBuildingSubmission ??
      this.state.currentLandscapeSubmission
    );
  }

  getCurrentBuildingSubmission(): TeamSubmission | null {
    return this.state.currentBuildingSubmission;
  }

  getCurrentLandscapeSubmission(): TeamSubmission | null {
    return this.state.currentLandscapeSubmission;
  }

  getSubmissions(): TeamSubmission[] {
    return this.state.currentParcel?.submissions || [];
  }

  updateCurrentParcelSubmissions(submissions: TeamSubmission[]): void {
    if (!this.state.currentParcel) return;
    this.updateParcelSubmissions(this.state.currentParcel.id, submissions);
  }

  updateParcelSubmissions(
    parcelId: string,
    submissions: TeamSubmission[],
  ): void {
    if (!this.state.project) return;

    const targetParcel = this.state.project.parcels.find(
      (parcel) => parcel.id === parcelId,
    );
    if (!targetParcel) return;

    const updatedParcel: Parcel = {
      ...targetParcel,
      submissions,
    };

    const updatedProject = {
      ...this.state.project,
      parcels: this.state.project.parcels.map((parcel) =>
        parcel.id === updatedParcel.id ? updatedParcel : parcel,
      ),
    };

    const currentBuildingSubmission =
      updatedParcel.submissions.find(
        (s) => s.id === this.state.currentBuildingSubmission?.id,
      ) ?? null;

    const currentLandscapeSubmission =
      updatedParcel.submissions.find(
        (s) => s.id === this.state.currentLandscapeSubmission?.id,
      ) ?? null;

    this.cleanupSubmissionSelectionForParcel(updatedParcel);

    this.updateState({
      currentParcel:
        this.state.currentParcel?.id === updatedParcel.id
          ? updatedParcel
          : this.state.currentParcel,
      project: updatedProject,
      currentBuildingSubmission,
      currentLandscapeSubmission,
    });
  }

  removeSubmissionFromParcel(parcelId: string, submissionId: string): Promise<void> {
    return this.enqueueMutation(() => this.removeSubmissionFromParcelInternal(parcelId, submissionId));
  }

  private async removeSubmissionFromParcelInternal(
    parcelId: string,
    submissionId: string,
  ): Promise<void> {
    const submission = this.findSubmissionInLoadedState(parcelId, submissionId);
    if (!submission) {
      return;
    }

    await this.unloadSubmission(submission, parcelId);
    const parcel = this.state.project?.parcels.find(item => item.id === parcelId);
    if (parcel) this.updateParcelSubmissions(parcelId, parcel.submissions.filter(item => item.id !== submissionId));
  }

  getCurrentParcel(): Parcel | null {
    return this.state.currentParcel;
  }

  switchToParcel(parcelId: string): Promise<void> {
    return this.enqueueMutation(() => this.switchToParcelInternal(parcelId));
  }

  private async switchToParcelInternal(parcelId: string): Promise<void> {
    if (!this.state.project) return;

    const parcel = this.state.project.parcels.find((p) => p.id === parcelId);
    if (!parcel) return;

    if (this.state.currentParcel?.id === parcelId) {
      return;
    }

    this.updateState({
      currentParcel: parcel,
      currentBuildingSubmission: null,
      currentLandscapeSubmission: null,
      isSubmissionLoading: false,
      buildingSubmissionError: null,
      landscapeSubmissionError: null,
    });

    const loadPromises: Promise<SubmissionLoadResult>[] = [];

    const firstBuilding = this.findSelectedSubmissionByType(parcel, "BUILDING");
    if (firstBuilding) {
      loadPromises.push(this.loadSubmissionInternal(firstBuilding));
    }

    const firstLandscape = this.findSelectedSubmissionByType(
      parcel,
      "LANDSCAPE",
    );
    if (firstLandscape) {
      loadPromises.push(this.loadSubmissionInternal(firstLandscape));
    }

    await Promise.all(loadPromises);
  }

  onStateChange(callback: (state: ProjectState) => void): () => void {
    this.onStateChangeCallbacks.push(callback);

    return () => {
      const index = this.onStateChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.onStateChangeCallbacks.splice(index, 1);
      }
    };
  }

  private updateState(updates: Partial<ProjectState>): void {
    this.state = { ...this.state, ...updates, selectedSubmissionIds: this.getLoadedSubmissions().map((item) => item.id) };

    for (const callback of this.onStateChangeCallbacks) {
      try {
        callback(this.state);
      } catch (error) {
        console.error("[ProjectManager] Erreur dans onStateChange:", error);
      }
    }
  }

  async dispose(): Promise<void> {
    // Attendre explicitement le déchargement du projet avant de disposer le LayerManager
    // pour éviter les race conditions lors du rechargement du viewer
    await this.unloadProject();
    await this.layerManager.dispose();
    this.onStateChangeCallbacks = [];
  }

  private findSelectedSubmissionByType(
    parcel: Parcel,
    type: SubmissionType,
  ): TeamSubmission | null {
    // 1. D'abord, vérifier la sélection déjà en mémoire (élevée en priorité)
    const selectedSubmissionId = this.getSelectedSubmissionId(parcel.id, type);

    if (selectedSubmissionId) {
      const selected = parcel.submissions.find(
        (submission) =>
          submission.id === selectedSubmissionId &&
          submission.submissionType === type,
      );
      if (selected) {
        return selected;
      }
    }

    // 2. Ensuite, vérifier le storage de persistance (pour les restau après remount)
    if (this.state.project) {
      const persistedId = useSelectionPersistenceStore.getState().getSelection(
        this.state.project.id,
        parcel.id,
        type,
      );

      if (persistedId) {
        const persisted = parcel.submissions.find(
          (submission) =>
            submission.id === persistedId && submission.submissionType === type,
        );
        if (persisted) {
          return persisted;
        }
      }
    }

    // 3. Pour les équipes, retomber sur leur dernière soumission si aucune sélection n'est restaurable.
    const currentUser = useAuthStore.getState().user;
    const saved = useSelectionPersistenceStore.getState().persistedSelections;
    const hasSavedChoice = this.state.project && saved && Object.prototype.hasOwnProperty.call(saved, this.state.project.id);
    if (currentUser?.role === "TEAM" && !hasSavedChoice) {
      const fallbackSubmission = parcel.submissions.find(
        (submission) =>
          submission.teamId === currentUser.id && submission.submissionType === type,
      );
      if (fallbackSubmission) {
        return fallbackSubmission;
      }
    }

    if (this.selectFirstSubmissions && (currentUser?.role === "CLIENT" || currentUser?.role === "ADMIN")) {
      return parcel.submissions.find(submission => submission.submissionType === type) ?? null;
    }

    // 4. Aucune sélection restaurable - retourner null
    return null;
  }

  /**
   * Restaure les sélections persistées dans sessionStorage après un chargement de projet.
   * Cette méthode est appelée après le chargement initial pour s'assurer que les
   * visualisations sont bien restaurées même si elles n'étaient pas dans les targets initiaux.
   */
  private async restorePersistedSelections(
    selections: ReturnType<typeof getSelectionsToRestore>,
  ): Promise<void> {
    const selectionsToRestore = this.state.allowMultipleSubmissions ? selections : selections.filter((item, index) =>
      !selections.slice(index + 1).some((next) => next.parcelId === item.parcelId && next.type === item.type),
    );

    if (selectionsToRestore.length === 0) return;

    // Pour chaque sélection persistée, vérifier si elle est déjà chargée
    // Si non, la charger silencieusement
    for (const { parcelId, type, submissionId } of selectionsToRestore) {
      // Vérifier si déjà chargé comme soumission courante

      // Trouver la soumission dans les données du projet
      const parcel = this.state.project?.parcels.find(
        (p) => p.id === parcelId,
      );
      if (!parcel) continue;

      const submission = parcel.submissions.find(
        (s) => s.id === submissionId && s.submissionType === type,
      );
      if (!submission) continue;

      // Charger silencieusement sans déclencher de progression visible
      const result = await this.loadSubmissionForParcel(submission, parcel);
      if (!result.success) continue;

      // Appliquer l'opacité actuelle
      if (submission.architectureLayer) {
        this.layerManager.setLayerOpacity(
          submission.architectureLayer.id,
          this.state.architectureOpacity,
        );
      }
    }
  }

  private findSubmissionWithParcel(
    submissionId: string,
  ): { submission: TeamSubmission; parcel: Parcel } | null {
    if (!this.state.project) {
      return null;
    }

    for (const parcel of this.state.project.parcels) {
      const submission = parcel.submissions.find(
        (candidate) => candidate.id === submissionId,
      );
      if (submission) {
        return { submission, parcel };
      }
    }

    return null;
  }

  private findSubmissionInLoadedState(
    parcelId: string,
    submissionId: string,
  ): TeamSubmission | null {
    if (!this.state.project) {
      return null;
    }

    const parcel = this.state.project.parcels.find((item) => item.id === parcelId);
    if (!parcel) {
      return null;
    }

    return (
      parcel.submissions.find((submission) => submission.id === submissionId) ??
      null
    );
  }

  private beginSubmissionLoading(): void {
    this.submissionLoadCount += 1;
    if (!this.state.isSubmissionLoading) {
      this.updateState({ isSubmissionLoading: true });
    }
  }

  private endSubmissionLoading(): void {
    this.submissionLoadCount = Math.max(0, this.submissionLoadCount - 1);
    if (this.submissionLoadCount === 0 && this.state.isSubmissionLoading) {
      this.updateState({ isSubmissionLoading: false });
    }
  }

  private async loadSubmissionForParcel(
    submission: TeamSubmission,
    parcel: Parcel,
    onModelLoadingProgress?: (progress: ModelLoadingProgress) => void,
    options: LoadSubmissionForParcelOptions = {},
  ): Promise<SubmissionLoadResult> {
    if (!this.state.project) return { success: false, errorMessage: "Aucun projet actif." };
    if (!this.state.project.parcels.some(p => p.id === parcel.id && p.submissions.some(s => s.id === submission.id))) {
      return { success: false, errorMessage: "Cette soumission ne fait pas partie de la phase consultée." };
    }
    const existing = this.loadedSubmissionByParcelId.get(parcel.id)?.[submission.submissionType]?.find((item) => item.id === submission.id) ?? null;
    const reload = this.shouldForceReloadSubmission(existing, submission);
    const submissionErrors = { ...this.state.submissionErrors };
    delete submissionErrors[submission.id];
    this.updateState({ submissionErrors });
    this.setSubmissionError(submission.submissionType, null);
    this.beginSubmissionLoading();
    try {
      if (!existing || reload) {
        if (reload && existing) await this.unloadSubmission(existing, parcel.id);
        if (submission.architectureLayer) {
          const solar = useSolarSimulationStore.getState();
          const solarAnchor = parcel.referenceWgs84 ?? (
            typeof this.state.project.defaultLongitude === "number" && typeof this.state.project.defaultLatitude === "number"
              ? { longitude: this.state.project.defaultLongitude, latitude: this.state.project.defaultLatitude } : null
          );
          // Charger d'abord la nouvelle proposition : un échec ne retire pas les autres.
          await this.layerManager.loadLayer(submission.architectureLayer, this.state.project.id, {
            referenceWgs84: parcel.referenceWgs84 ?? null,
            solarAnchor,
            solarContext: { hour: solar.hour, season: solar.season },
            referenceRotationDeg: parcel.refRotation ?? null,
            referencePitchDeg: parcel.refPitch ?? null,
            referenceRollDeg: parcel.refRoll ?? null,
            teamParcelModelCorrection: submission.teamParcelModelCorrection ?? null,
            submissionType: submission.submissionType,
            onModelLoadingProgress,
          });
          this.layerManager.setLayerOpacity(submission.architectureLayer.id, this.state.architectureOpacity);
        }
        if (!this.state.allowMultipleSubmissions) {
          await this.unloadSubmissionForParcelByType(parcel.id, submission.submissionType);
        }
      }
      this.saveLoadedSubmission(parcel.id, submission.submissionType, submission);
      this.saveSubmissionSelection(parcel.id, submission.submissionType, submission.id);
      if (options.setAsCurrent !== false) {
        this.updateState(submission.submissionType === "BUILDING"
          ? { currentBuildingSubmission: submission } : { currentLandscapeSubmission: submission });
      }
      this.applyViewMode(this.state.viewMode, { preserveGabaritVisibility: true });
      return { success: true };
    } catch (error) {
      const message = this.toSubmissionLoadErrorMessage(error);
      this.updateState({ submissionErrors: { ...this.state.submissionErrors, [submission.id]: message } });
      this.setSubmissionError(submission.submissionType, message);
      return { success: false, errorMessage: message };
    } finally {
      this.endSubmissionLoading();
    }
  }

  private shouldForceReloadSubmission(
    currentLoadedSubmission: TeamSubmission | null,
    nextSubmission: TeamSubmission,
  ): boolean {
    if (currentLoadedSubmission?.id !== nextSubmission.id) {
      return false;
    }

    return (
      currentLoadedSubmission.updatedAt !== nextSubmission.updatedAt ||
      currentLoadedSubmission.version !== nextSubmission.version ||
      currentLoadedSubmission.architectureLayer?.id !==
        nextSubmission.architectureLayer?.id
    );
  }

  private saveLoadedSubmission(
    parcelId: string,
    type: SubmissionType,
    submission: TeamSubmission,
  ): void {
    this.selectionOrder = [...this.selectionOrder.filter((id) => id !== submission.id), submission.id];
    const previousSelection =
      this.loadedSubmissionByParcelId.get(parcelId) ?? {};
    this.loadedSubmissionByParcelId.set(parcelId, {
      ...previousSelection,
      [type]: [...(previousSelection[type] ?? []).filter((item) => item.id !== submission.id), submission],
    });
  }

  private getLoadedSubmissions(): TeamSubmission[] {
    return Array.from(this.loadedSubmissionByParcelId.values()).flatMap((types) => [
      ...(types.BUILDING ?? []), ...(types.LANDSCAPE ?? []),
    ]).sort((left, right) => this.selectionOrder.indexOf(left.id) - this.selectionOrder.indexOf(right.id));
  }

  private getLoadedArchitectureLayerIds(): string[] {
    return [...new Set([
      ...this.getLoadedSubmissions().flatMap((submission) => submission.architectureLayer ? [submission.architectureLayer.id] : []),
      ...(this.state.project?.overlayLayers ?? []).map((layer) => layer.id),
    ])];
  }

  private getSelectedSubmissionId(
    parcelId: string,
    type: SubmissionType,
  ): string | null {
    return this.submissionSelectionByParcelId.get(parcelId)?.[type] ?? null;
  }

  private saveSubmissionSelection(
    parcelId: string,
    type: SubmissionType,
    submissionId: string,
  ): void {
    const previousSelection =
      this.submissionSelectionByParcelId.get(parcelId) ?? {};
    this.submissionSelectionByParcelId.set(parcelId, {
      ...previousSelection,
      [type]: submissionId,
    });

    // Persister également dans sessionStorage pour restauration après remount
    if (this.state.project) {
      useSelectionPersistenceStore.getState().setActiveSelection(
        this.state.project.id,
        parcelId,
        submissionId,
        type,
        this.state.allowMultipleSubmissions ?? false,
      );
    }
  }

  private cleanupSubmissionSelectionForParcel(parcel: Parcel): void {
    const currentSelection = this.submissionSelectionByParcelId.get(parcel.id);
    if (!currentSelection) {
      return;
    }

    const validBuildingId = parcel.submissions.some(
      (submission) =>
        submission.id === currentSelection.BUILDING &&
        submission.submissionType === "BUILDING",
    )
      ? currentSelection.BUILDING
      : undefined;

    const validLandscapeId = parcel.submissions.some(
      (submission) =>
        submission.id === currentSelection.LANDSCAPE &&
        submission.submissionType === "LANDSCAPE",
    )
      ? currentSelection.LANDSCAPE
      : undefined;

    if (!validBuildingId && !validLandscapeId) {
      this.submissionSelectionByParcelId.delete(parcel.id);
      // Synchroniser le nettoyage des selections invalides dans le storage
      if (this.state.project) {
        const allSubmissionIds = new Set(
          this.state.project.parcels.flatMap((item) => item.submissions.map((s) => s.id)),
        );
        useSelectionPersistenceStore
          .getState()
          .pruneInvalidSelections(this.state.project.id, allSubmissionIds);
      }
      return;
    }

    const updatedSelection = {
      ...(validBuildingId ? { BUILDING: validBuildingId } : {}),
      ...(validLandscapeId ? { LANDSCAPE: validLandscapeId } : {}),
    };

    this.submissionSelectionByParcelId.set(parcel.id, updatedSelection);

    // Synchroniser aussi les selections dans le storage
    if (this.state.project) {
      for (const [type, submissionId] of Object.entries(updatedSelection)) {
        if (submissionId) {
          useSelectionPersistenceStore.getState().setActiveSelection(
            this.state.project.id,
            parcel.id,
            submissionId,
            type as SubmissionType,
            this.state.allowMultipleSubmissions ?? false,
          );
        }
      }
    }
  }

  private getOrderedStaticLayerItems(
    project: Project,
  ): OrderedStaticLayerItem[] {
    const items: OrderedStaticLayerItem[] = [];

    const simulationSolaire = useSolarSimulationStore.getState();

    for (const parcel of project.parcels) {
      const solarAnchor = parcel.referenceWgs84
        ? {
            longitude: parcel.referenceWgs84.longitude,
            latitude: parcel.referenceWgs84.latitude,
          }
        : typeof project.defaultLongitude === "number" &&
            typeof project.defaultLatitude === "number"
          ? {
              longitude: project.defaultLongitude,
              latitude: project.defaultLatitude,
            }
          : null;

      if (parcel.basemapLayer) {
        items.push({
          layer: parcel.basemapLayer,
          projectId: project.id,
          staticType: "basemap",
          context: {
            referenceWgs84: parcel.referenceWgs84 ?? null,
            solarAnchor,
            solarContext: {
              hour: simulationSolaire.hour,
              season: simulationSolaire.season,
            },
            referenceRotationDeg: parcel.refRotation ?? null,
            referencePitchDeg: parcel.refPitch ?? null,
            referenceRollDeg: parcel.refRoll ?? null,
          },
        });
      }

      if (parcel.gabaritLayer) {
        items.push({
          layer: parcel.gabaritLayer,
          projectId: project.id,
          staticType: "gabarit",
          context: {
            referenceWgs84: parcel.referenceWgs84 ?? null,
            solarAnchor,
            solarContext: {
              hour: simulationSolaire.hour,
              season: simulationSolaire.season,
            },
            referenceRotationDeg: parcel.refRotation ?? null,
            referencePitchDeg: parcel.refPitch ?? null,
            referenceRollDeg: parcel.refRoll ?? null,
          },
        });
      }
    }

    const parcelReference = this.getReferenceParcel(project);

    if (parcelReference && project.overlayLayers) {
      const solarAnchor = parcelReference.referenceWgs84
        ? {
            longitude: parcelReference.referenceWgs84.longitude,
            latitude: parcelReference.referenceWgs84.latitude,
          }
        : typeof project.defaultLongitude === "number" &&
            typeof project.defaultLatitude === "number"
          ? {
              longitude: project.defaultLongitude,
              latitude: project.defaultLatitude,
            }
          : null;

      for (const overlayLayer of project.overlayLayers) {
        items.push({
          layer: overlayLayer,
          projectId: project.id,
          staticType: "overlay",
          context: {
            referenceWgs84: parcelReference.referenceWgs84 ?? null,
            solarAnchor,
            solarContext: {
              hour: simulationSolaire.hour,
              season: simulationSolaire.season,
            },
            referenceRotationDeg: parcelReference.refRotation ?? null,
            referencePitchDeg: parcelReference.refPitch ?? null,
            referenceRollDeg: parcelReference.refRoll ?? null,
            overlayAlignment: getProjectOverlayAlignment(overlayLayer),
          },
        });
      }
    }

    const staticTypePriority: Record<ParcelStaticLayerType, number> = {
      basemap: 0,
      gabarit: 1,
      overlay: 2,
    };

    return items.sort((a, b) => {
      const zOrderA = a.layer?.zOrder ?? 0;
      const zOrderB = b.layer?.zOrder ?? 0;

      if (zOrderA !== zOrderB) {
        return zOrderA - zOrderB;
      }

      return (
        staticTypePriority[a.staticType] - staticTypePriority[b.staticType]
      );
    });
  }

  private async loadProjectStaticLayersInExplicitOrder(
    project: Project,
    options: {
      includeGabarits?: boolean;
    } = {},
  ): Promise<void> {
    const includeGabarits = options.includeGabarits ?? true;
    const orderedItems = this.getOrderedStaticLayerItems(project).filter(
      (item) => includeGabarits || item.staticType !== "gabarit",
    );

    for (const item of orderedItems) {
      if (!item.layer) {
        continue;
      }

      await this.layerManager.loadLayer(item.layer, item.projectId, {
        referenceWgs84: item.context?.referenceWgs84,
        solarAnchor: item.context?.solarAnchor,
        solarContext: item.context?.solarContext,
        referenceRotationDeg: item.context?.referenceRotationDeg,
        referencePitchDeg: item.context?.referencePitchDeg,
        referenceRollDeg: item.context?.referenceRollDeg,
        overlayAlignment: item.context?.overlayAlignment,
      });

      if (item.staticType === "basemap") {
        this.layerManager.setLayerOpacity(item.layer.id, this.state.basemapOpacity);
      }

      if (item.staticType === "gabarit") {
        this.layerManager.setLayerOpacity(item.layer.id, this.state.gabaritOpacity);
      }
    }

    this.updateState({
      gabaritsLoaded: includeGabarits && this.hasProjectParcelLayer("gabarit"),
      gabaritVisible:
        includeGabarits &&
        this.hasProjectParcelLayer("gabarit") &&
        this.state.viewMode === "after",
    });
  }

  private getProjectParcelLayerIds(type: ParcelStaticLayerType): string[] {
    if (!this.state.project) {
      return [];
    }

    const layerIds = new Set<string>();

    for (const parcel of this.state.project.parcels) {
      const layer =
        type === "basemap" ? parcel.basemapLayer : parcel.gabaritLayer;
      if (layer?.id) {
        layerIds.add(layer.id);
      }
    }

    return Array.from(layerIds);
  }

  private getReferenceParcel(project: Project): Parcel | null {
    const overlayParcelId = project.overlayLayers?.[0]?.asset?.parcelId;

    if (overlayParcelId) {
      const overlayParcel =
        project.parcels.find((parcel) => parcel.id === overlayParcelId) ?? null;

      if (overlayParcel) {
        return overlayParcel;
      }
    }

    return project.parcels[0] ?? null;
  }

  private getProjectOverlayLayerIds(): string[] {
    if (!this.state.project) {
      return [];
    }

    return this.state.project.overlayLayers?.map((layer) => layer.id) ?? [];
  }

  private hasProjectParcelLayer(type: ParcelStaticLayerType): boolean {
    return this.getProjectParcelLayerIds(type).length > 0;
  }

  private applyToProjectParcelLayers(
    type: ParcelStaticLayerType,
    action: (layerId: string) => void,
  ): void {
    const layerIds = this.getProjectParcelLayerIds(type);
    for (const layerId of layerIds) {
      if (!this.layerManager.getLayerState(layerId)) {
        continue;
      }
      action(layerId);
    }
  }

  private applyToProjectOverlayLayers(action: (layerId: string) => void): void {
    const layerIds = this.getProjectOverlayLayerIds();
    for (const layerId of layerIds) {
      if (!this.layerManager.getLayerState(layerId)) {
        continue;
      }
      action(layerId);
    }
  }

  private async unloadStaticLayers(project: Project): Promise<void> {
    const layerIds = new Set<string>();

    for (const parcel of project.parcels) {
      if (parcel.gabaritLayer?.id) {
        layerIds.add(parcel.gabaritLayer.id);
      }
      if (parcel.basemapLayer?.id) {
        layerIds.add(parcel.basemapLayer.id);
      }
    }

    for (const overlayLayer of project.overlayLayers ?? []) {
      layerIds.add(overlayLayer.id);
    }

    for (const layerId of layerIds) {
      if (!this.layerManager.getLayerState(layerId)) {
        continue;
      }
      await this.layerManager.unloadLayer(layerId);
    }
  }

  private async unloadSubmission(submission: TeamSubmission, parcelId: string): Promise<void> {
    if (submission.architectureLayer) await this.layerManager.unloadLayer(submission.architectureLayer.id);
    const types = this.loadedSubmissionByParcelId.get(parcelId) ?? {};
    const remaining = (types[submission.submissionType] ?? []).filter((item) => item.id !== submission.id);
    if (remaining.length) types[submission.submissionType] = remaining;
    else delete types[submission.submissionType];
    if (Object.keys(types).length) this.loadedSubmissionByParcelId.set(parcelId, types);
    else this.loadedSubmissionByParcelId.delete(parcelId);
    this.selectionOrder = this.selectionOrder.filter((id) => id !== submission.id);
    const last = this.getLoadedSubmissions().filter((item) => item.submissionType === submission.submissionType).slice(-1)[0] ?? null;
    const lastForParcel = remaining.slice(-1)[0] ?? null;
    if (lastForParcel) this.submissionSelectionByParcelId.set(parcelId, { ...this.submissionSelectionByParcelId.get(parcelId), [submission.submissionType]: lastForParcel.id });
    else {
      const selection = this.submissionSelectionByParcelId.get(parcelId);
      if (selection) delete selection[submission.submissionType];
    }
    if (this.state.project) useSelectionPersistenceStore.getState().clearSelection(this.state.project.id, parcelId, submission.submissionType, submission.id);
    this.updateState(submission.submissionType === "BUILDING" && this.state.currentBuildingSubmission?.id === submission.id
      ? { currentBuildingSubmission: last }
      : submission.submissionType === "LANDSCAPE" && this.state.currentLandscapeSubmission?.id === submission.id
        ? { currentLandscapeSubmission: last } : {});
    this.cesiumManager.requestRender();
  }

  private async unloadSubmissionForParcelByType(parcelId: string, type: SubmissionType): Promise<void> {
    for (const submission of [...(this.loadedSubmissionByParcelId.get(parcelId)?.[type] ?? [])]) {
      await this.unloadSubmission(submission, parcelId);
    }
  }

  private async unloadCurrentSubmissionLayers(): Promise<void> {
    // Les overlays du projet appartiennent au décor, pas aux propositions.
    for (const submission of this.getLoadedSubmissions()) {
      if (submission.architectureLayer) await this.layerManager.unloadLayer(submission.architectureLayer.id);
    }
    this.loadedSubmissionByParcelId.clear();
    this.selectionOrder = [];
  }

  private applyToActiveArchitectureLayers(
    action: (layerId: string) => void,
  ): void {
    for (const layerId of this.getLoadedArchitectureLayerIds()) {
      action(layerId);
    }
  }

  private hasActiveArchitectureLayers(): boolean {
    return this.getLoadedArchitectureLayerIds().length > 0;
  }

  private async unloadAllGabarits(): Promise<void> {
    for (const layerId of this.getProjectParcelLayerIds("gabarit")) {
      if (!this.layerManager.getLayerState(layerId)) {
        continue;
      }

      await this.layerManager.unloadLayer(layerId);
    }

    this.updateState({
      gabaritsLoaded: false,
      gabaritVisible: false,
    });
    this.cesiumManager.requestRender();
  }

  private setSubmissionError(
    type: SubmissionType,
    message: string | null,
  ): void {
    if (type === "BUILDING") {
      this.updateState({ buildingSubmissionError: message });
      return;
    }
    this.updateState({ landscapeSubmissionError: message });
  }

  private toSubmissionLoadErrorMessage(error: unknown): string {
    const rawMessage =
      error instanceof Error
        ? error.message.toLowerCase()
        : String(error).toLowerCase();

    if (
      rawMessage.includes("draco decoder could not be initialized") ||
      rawMessage.includes("webassembly.instantiate(): out of memory")
    ) {
      return "Le modèle 3D n'a pas pu être chargé malgré une tentative de fallback sans Draco. Fermez les onglets/extensions gourmands puis rechargez la page.";
    }

    if (rawMessage.includes("failed to load model")) {
      return "Le modèle 3D n'a pas pu être chargé. Vérifiez le fichier source et réessayez.";
    }

    return "Erreur lors du chargement du modèle.";
  }
}
